import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Cloud, Radio, Activity, RefreshCw, AlertTriangle, CheckCircle, XCircle, Hexagon, Zap, Heart, Settings, X, Plus, Trash2, ArrowLeft } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Corner Bracket Component
const CornerBrackets = () => (
  <>
    <div className="corner-bracket corner-tl" />
    <div className="corner-bracket corner-tr" />
    <div className="corner-bracket corner-bl" />
    <div className="corner-bracket corner-br" />
  </>
);

// Status Indicator Component
const StatusIndicator = ({ status }) => {
  const statusClasses = {
    operational: "status-operational",
    degraded: "status-degraded",
    outage: "status-outage",
    unknown: "bg-gray-500"
  };

  const statusText = {
    operational: "ONLINE",
    degraded: "DEGRADED",
    outage: "OFFLINE",
    unknown: "UNKNOWN"
  };

  const textColor = {
    operational: "text-green-400",
    degraded: "text-amber-500",
    outage: "text-rose-500",
    unknown: "text-gray-400"
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${statusClasses[status] || statusClasses.unknown}`} />
      <span className={`text-xs tracking-widest uppercase ${textColor[status] || textColor.unknown}`}>
        {statusText[status] || statusText.unknown}
      </span>
    </div>
  );
};

// Service Row Component
const ServiceRow = ({ name, status, url }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer"
    className="flex items-center py-2 hover:bg-cyan-950/20 transition-colors group cursor-pointer"
    data-testid={`service-row-${name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <span className="text-sm text-cyan-100 group-hover:text-cyan-400 transition-colors whitespace-nowrap">
      {name}
    </span>
    <div className="leader-dots" />
    <StatusIndicator status={status} />
  </a>
);

// Arc Reactor Core Component
const ArcReactorCore = ({ systemStatus }) => {
  const statusColors = {
    NOMINAL: { ring: "border-cyan-400/50", glow: "shadow-[0_0_60px_rgba(0,229,255,0.3)]", text: "text-cyan-400" },
    DEGRADED: { ring: "border-amber-500/50", glow: "shadow-[0_0_60px_rgba(255,159,28,0.3)]", text: "text-amber-500" },
    CRITICAL: { ring: "border-rose-500/50", glow: "shadow-[0_0_60px_rgba(255,51,102,0.3)]", text: "text-rose-500" }
  };

  const colors = statusColors[systemStatus] || statusColors.NOMINAL;

  return (
    <div className="relative w-full h-full flex items-center justify-center min-h-[250px]" data-testid="arc-reactor-core">
      <div className={`arc-ring w-56 h-56 animate-spin-slow ${colors.ring}`} />
      <div className={`arc-ring w-44 h-44 animate-spin-reverse border-dashed ${colors.ring}`} />
      <div className={`arc-ring w-32 h-32 animate-spin-slow ${colors.ring}`} />
      <div className={`absolute w-20 h-20 rounded-full bg-black/80 border ${colors.ring} ${colors.glow} flex items-center justify-center`}>
        <Hexagon className={`w-8 h-8 ${colors.text}`} strokeWidth={1.5} />
      </div>
      <div className="absolute bottom-0 left-0 right-0 text-center pb-4">
        <p className="text-xs tracking-[0.2em] text-cyan-500/70 uppercase mb-1">System Status</p>
        <p className={`text-2xl font-bold tracking-wider ${colors.text} text-glow font-sans`}>
          {systemStatus}
        </p>
      </div>
    </div>
  );
};

// Panel Component with corner brackets
const Panel = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`glass-panel relative p-4 ${className}`} data-testid={`panel-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <CornerBrackets />
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />}
      <h2 className="text-lg tracking-[0.15em] uppercase font-semibold text-cyan-400 font-sans">
        {title}
      </h2>
    </div>
    {children}
  </div>
);

// Data Stream / Event Log Component
const DataStream = ({ events }) => {
  return (
    <div className="h-full overflow-hidden relative font-mono" data-testid="data-stream">
      <div className="space-y-1 text-xs">
        {events.map((event, index) => (
          <div 
            key={index} 
            className="flex items-center gap-2 text-cyan-500/80 animate-pulse"
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <span className="text-cyan-600">[SYS]</span>
            <span>{event.message}</span>
          </div>
        ))}
      </div>
      <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({ open, onOpenChange, settings, onSaveSettings, services }) => {
  const [recipients, setRecipients] = useState(settings.recipients || []);
  const [newEmail, setNewEmail] = useState("");
  const [selectedServices, setSelectedServices] = useState(settings.alertServices || []);

  useEffect(() => {
    setRecipients(settings.recipients || []);
    setSelectedServices(settings.alertServices || []);
  }, [settings]);

  const addRecipient = () => {
    if (newEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      if (!recipients.includes(newEmail)) {
        setRecipients([...recipients, newEmail]);
        setNewEmail("");
      } else {
        toast.error("Email already added");
      }
    } else {
      toast.error("Please enter a valid email address");
    }
  };

  const removeRecipient = (email) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  const toggleService = (serviceId) => {
    if (selectedServices.includes(serviceId)) {
      setSelectedServices(selectedServices.filter(s => s !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const handleSave = () => {
    onSaveSettings({ recipients, alertServices: selectedServices });
    toast.success("Settings saved successfully");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#030712] border border-cyan-500/30 text-cyan-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl tracking-[0.15em] uppercase font-semibold text-cyan-400 font-sans flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Alert Settings
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Email Recipients */}
          <div>
            <h3 className="text-sm tracking-[0.15em] uppercase text-cyan-500 mb-3">Email Recipients</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                placeholder="Enter email address"
                className="flex-1 bg-black/50 border border-cyan-500/30 text-cyan-100 px-3 py-2 text-sm focus:outline-none focus:border-cyan-400 placeholder:text-cyan-500/50"
                data-testid="email-input"
              />
              <button
                onClick={addRecipient}
                className="border border-cyan-500/50 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-400 hover:text-black transition-all px-3 py-2"
                data-testid="add-email-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {recipients.length === 0 ? (
                <p className="text-xs text-cyan-500/50 italic">No recipients added</p>
              ) : (
                recipients.map((email, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-black/30 border border-cyan-500/20 px-3 py-2">
                    <span className="text-sm text-cyan-100">{email}</span>
                    <button
                      onClick={() => removeRecipient(email)}
                      className="text-rose-500 hover:text-rose-400 transition-colors"
                      data-testid={`remove-email-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Service Selection */}
          <div>
            <h3 className="text-sm tracking-[0.15em] uppercase text-cyan-500 mb-3">Alert for Services</h3>
            <div className="grid grid-cols-2 gap-2">
              {services.map((service) => (
                <label
                  key={service.id}
                  className="flex items-center gap-2 bg-black/30 border border-cyan-500/20 px-3 py-2 cursor-pointer hover:bg-cyan-950/20 transition-colors"
                >
                  <Checkbox
                    checked={selectedServices.includes(service.id)}
                    onCheckedChange={() => toggleService(service.id)}
                    className="border-cyan-500/50 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                  />
                  <span className="text-sm text-cyan-100">{service.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="w-full border border-cyan-500/50 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_20px_#00E5FF] transition-all uppercase tracking-[0.2em] text-sm py-3"
            data-testid="save-settings-btn"
          >
            Save Settings
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Format date/time for display
const formatDateTime = (date) => {
  if (!date) return "---";
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Main Dashboard
function App() {
  const [services, setServices] = useState([]);
  const [systemStatus, setSystemStatus] = useState("NOMINAL");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [alertSettings, setAlertSettings] = useState({ recipients: [], alertServices: [] });

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/status`);
      setServices(response.data.services);
      setSystemStatus(response.data.system_status);
      setLastUpdated(new Date());
      
      // Check for any issues and notify
      const issues = response.data.services.filter(s => s.status !== "operational");
      if (issues.length > 0) {
        issues.forEach(service => {
          toast.warning(`${service.name}: ${service.status.toUpperCase()}`, {
            description: service.description
          });
        });
      }
    } catch (e) {
      console.error("Failed to fetch status:", e);
      toast.error("Failed to fetch service status");
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (e) {
      console.error("Failed to fetch events:", e);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setAlertSettings(response.data);
    } catch (e) {
      console.error("Failed to fetch settings:", e);
    }
  }, []);

  const saveSettings = async (newSettings) => {
    try {
      await axios.post(`${API}/settings`, newSettings);
      setAlertSettings(newSettings);
    } catch (e) {
      console.error("Failed to save settings:", e);
      toast.error("Failed to save settings");
    }
  };

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStatus(), fetchEvents()]);
    setLoading(false);
  }, [fetchStatus, fetchEvents]);

  useEffect(() => {
    refreshAll();
    fetchSettings();
    
    const interval = setInterval(() => {
      refreshAll();
    }, refreshInterval * 1000);

    return () => clearInterval(interval);
  }, [refreshAll, fetchSettings, refreshInterval]);

  // Filter services by category
  const cloudServices = services.filter(s => s.category === "cloud");
  const cdnServices = services.filter(s => s.category === "cdn");
  const cellularServices = services.filter(s => s.category === "cellular");

  return (
    <div className="min-h-screen stark-bg relative overflow-hidden">
      <Toaster position="top-right" theme="dark" />
      
      {/* Scanning line effect */}
      <div className="scan-line" />
      
      {/* Header */}
      <header className="relative z-10 p-4 md:p-6 border-b border-cyan-500/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Back To Hub Button */}
            <a
              href="https://cardiac-solutions.ai/hub"
              className="border border-cyan-500/50 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_20px_#00E5FF] transition-all uppercase tracking-[0.2em] text-xs py-2 px-3 flex items-center gap-2"
              data-testid="back-to-hub-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back To Hub</span>
            </a>
            
            <Heart className="w-8 h-8 text-rose-500 animate-pulse" strokeWidth={1.5} fill="currentColor" />
            <div>
              <h1 className="text-2xl md:text-3xl tracking-tighter uppercase font-bold font-sans text-glow" data-testid="main-title">
                <span className="text-cyan-100">Cardiac</span> <span className="text-rose-500">Solutions</span>
              </h1>
              <p className="text-xs tracking-[0.2em] text-cyan-500/70 uppercase">
                Services Outage Monitoring
              </p>
            </div>
          </div>
          
          {/* As of timestamp - centered */}
          <div className="text-center" data-testid="as-of-timestamp">
            <p className="text-xs tracking-[0.2em] text-cyan-500/70 uppercase">As of</p>
            <p className="text-sm text-cyan-400 font-mono">{formatDateTime(lastUpdated)}</p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Refresh interval selector */}
            <div className="flex items-center gap-2 text-xs text-cyan-500/70">
              <span className="tracking-wider uppercase">Refresh:</span>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                className="bg-black/50 border border-cyan-500/30 text-cyan-400 px-2 py-1 text-xs uppercase tracking-wider focus:outline-none focus:border-cyan-400"
                data-testid="refresh-interval-select"
              >
                <option value={10}>10s</option>
                <option value={30}>30s</option>
                <option value={60}>60s</option>
                <option value={300}>5m</option>
              </select>
            </div>
            
            {/* Manual refresh button */}
            <button
              onClick={refreshAll}
              disabled={loading}
              className="border border-cyan-500/50 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_20px_#00E5FF] transition-all uppercase tracking-[0.2em] text-xs py-2 px-4 flex items-center gap-2 disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>

            {/* Settings button */}
            <button
              onClick={() => setSettingsOpen(true)}
              className="border border-cyan-500/50 bg-cyan-950/30 text-cyan-400 hover:bg-cyan-400 hover:text-black hover:shadow-[0_0_20px_#00E5FF] transition-all p-2"
              data-testid="settings-button"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={alertSettings}
        onSaveSettings={saveSettings}
        services={services}
      />

      {/* Main Grid */}
      <main className="relative z-10 p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          
          {/* Cloud Services Panel */}
          <div className="col-span-1 md:col-span-3 md:row-span-2">
            <Panel title="Cloud Services" icon={Cloud}>
              <div className="space-y-1">
                {cloudServices.map(service => (
                  <ServiceRow 
                    key={service.id} 
                    name={service.name} 
                    status={service.status}
                    url={service.url}
                  />
                ))}
              </div>
            </Panel>
          </div>

          {/* Center Core Status */}
          <div className="col-span-1 md:col-span-6 md:row-span-2">
            <Panel title="Core Status" icon={Hexagon} className="h-full">
              <ArcReactorCore systemStatus={systemStatus} />
              
              {/* Status summary */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="p-2 border border-cyan-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle className="w-3 h-3 text-cyan-400" />
                    <span className="text-xs text-cyan-500/70 uppercase tracking-wider">Online</span>
                  </div>
                  <p className="text-lg font-bold text-cyan-400 font-sans" data-testid="online-count">
                    {services.filter(s => s.status === "operational").length}
                  </p>
                </div>
                <div className="p-2 border border-amber-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                    <span className="text-xs text-amber-500/70 uppercase tracking-wider">Degraded</span>
                  </div>
                  <p className="text-lg font-bold text-amber-500 font-sans" data-testid="degraded-count">
                    {services.filter(s => s.status === "degraded").length}
                  </p>
                </div>
                <div className="p-2 border border-rose-500/20">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <XCircle className="w-3 h-3 text-rose-500" />
                    <span className="text-xs text-rose-500/70 uppercase tracking-wider">Outage</span>
                  </div>
                  <p className="text-lg font-bold text-rose-500 font-sans" data-testid="outage-count">
                    {services.filter(s => s.status === "outage").length}
                  </p>
                </div>
              </div>
            </Panel>
          </div>

          {/* Cellular Carriers Panel */}
          <div className="col-span-1 md:col-span-3 md:row-span-2">
            <Panel title="Cellular Carriers" icon={Radio}>
              <div className="space-y-1">
                {cellularServices.map(service => (
                  <ServiceRow 
                    key={service.id} 
                    name={service.name} 
                    status={service.status}
                    url={service.url}
                  />
                ))}
              </div>
              
              {/* CDN Section */}
              <div className="mt-6 pt-4 border-t border-cyan-500/20">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
                  <h3 className="text-sm tracking-[0.15em] uppercase font-semibold text-cyan-400 font-sans">
                    CDN
                  </h3>
                </div>
                <div className="space-y-1">
                  {cdnServices.map(service => (
                    <ServiceRow 
                      key={service.id} 
                      name={service.name} 
                      status={service.status}
                      url={service.url}
                    />
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          {/* Data Stream / Event Log */}
          <div className="col-span-1 md:col-span-12">
            <Panel title="Data Stream" icon={Activity} className="h-40">
              <DataStream events={events} />
            </Panel>
          </div>
        </div>
        
        {/* AED Context Footer */}
        <div className="mt-6 glass-panel relative p-4" data-testid="aed-context">
          <CornerBrackets />
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-cyan-400 animate-pulse" />
            <div>
              <p className="text-sm text-cyan-400 font-semibold tracking-wider uppercase font-sans">
                AED Monitoring Link Status
              </p>
              <p className="text-xs text-cyan-500/70 mt-1">
                This dashboard monitors critical infrastructure that supports AED (Automated External Defibrillator) 
                monitoring services. Early detection of cloud or network issues helps ensure continuous operation 
                of life-saving equipment connectivity.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Decorative line at bottom */}
      <div className="h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent w-full mt-6" />
    </div>
  );
}

export default App;
